import {
	OfferHeaderId,
	ProductDimension,
	ProductDimensionValue,
	ProductFamily,
	ProductShortDescription,
	OfferProduct,
	OfferHeader,
	Offer
} from './offer';

export const PREFIX_OFFER_PRODUCT_ID_EOFFER = 'e';
export const PREFIX_OFFER_PRODUCT_ID_CRM = 'c';
export const OFFER_TYPE = 'offertype';

export function offerHeaderIdCreate(id: OfferHeaderId) {
	if (id.externalId) {
		return `${ PREFIX_OFFER_PRODUCT_ID_CRM }${ id.externalId }`;
	}
	if (id.id) {
		return `${ PREFIX_OFFER_PRODUCT_ID_EOFFER }${ id.id }`;
	}
	throw new Error('offer id not found');
}

function productDimensionValueEquals(d1: ProductDimensionValue, d2: ProductDimensionValue) {
	return d1.unit === d2.unit && d1.value === d2.value;
}

function productDimensionEquals(d1: ProductDimension, d2: ProductDimension) {
	return productDimensionValueEquals(d1.thickness, d2.thickness)
		&& productDimensionValueEquals(d1.width, d2.width)
		&& (
			(d1.length === undefined && d2.length === undefined)
			|| (
				d1.length !== undefined && d2.length !== undefined
				&& productDimensionValueEquals(d1.length, d2.length)
			)
		);
}

function productFamilyEquals(f1: ProductFamily, f2: ProductFamily) {
	return f1.code === f2.code;
}

function productShortDescriptionEquals(p1: ProductShortDescription, p2: ProductShortDescription) {
	return p1.productRequestId === p2.productRequestId
		&& p1.configurationRef === p2.configurationRef
		&& p1.customerArticle === p2.customerArticle
		&& productDimensionEquals(p1.dimension, p2.dimension)
		&& productFamilyEquals(p1.family, p2.family)
		&& p1.productRef === p2.productRef
		&& p1.steelGrade === p2.steelGrade;
}

function offerProductEquals(p1: OfferProduct, p2: OfferProduct) {
	return p1.deliveryMill === p2.deliveryMill
		&& productShortDescriptionEquals(p1.description, p2.description)
		&& p1.feasibilityStatus === p2.feasibilityStatus
		&& p1.offerStatus === p2.offerStatus
		&& p1.price === p2.price
		&& p1.quantity === p2.quantity
		&& p1.rdd === p2.rdd;
}

function offerProductsEquals(ps1: OfferProduct[], ps2: OfferProduct[]) {
	return ps1.length === ps2.length
		&& ps1.filter(
			p1 => ps2.some(p2 => offerProductEquals(p1, p2))
		)
			.length === ps1.length;
}

function offerProductCopy(product: OfferProduct) {
	return {
		...product,
		description: { ...product.description }
	};
}

function offerHeaderEquals(h1: OfferHeader, h2: OfferHeader) {
	return h1.comment === h2.comment
		&& h1.currency === h2.currency
		&& h1.endUser === h2.endUser
		&& h1.idd === h2.idd
		&& h1.incoterm === h2.incoterm
		&& h1.periodEndMonth === h2.periodEndMonth
		&& h1.periodStartMonth === h2.periodStartMonth
		&& h1.pricingUnit === h2.pricingUnit
		&& h1.rfqRef === h2.rfqRef
		&& h1.shipTo === h2.shipTo
		&& h1.soldTo === h2.soldTo
		&& h1.tmode === h2.tmode;
}

export function offerEquals(o1: Offer, o2: Offer) {
	return offerHeaderEquals(o1.header, o2.header)
		&& (
			(o1.products === undefined && o2.products === undefined)
			|| (
				o1.products !== undefined && o2.products !== undefined
				&& offerProductsEquals(o1.products, o2.products)
			)
		);
}

export function offerCopy(offer: Offer) {
	const { header, products, listOfOfferFamilyVolume } = offer;
	const mapproducts = products ? products.map(offerProductCopy) : undefined;
	return { header, mapproducts, listOfOfferFamilyVolume };
}

export function offerCreateDefault() {
	return {
		header: {}
	};
}

function createNextRfqRef(rfqRef: string | undefined) {
	if (!rfqRef) {
		return undefined;
	}
	if (rfqRef.endsWith('- copy')) {
		return `${ rfqRef } (2)`;
	}
	const result = rfqRef.match(/- copy\s*.\d+./g);
	if (result && result.length > 0) {
		const index = result[result.length - 1].match(/\d+/g);
		if (index && index.length > 0) {
			return `${
				rfqRef.slice(0, Math.max(0, rfqRef.lastIndexOf('(')))
			} (${
				Number(index[0]) + 1
			})`;
		}
		return `${ rfqRef } - copy`;
	}
	return `${ rfqRef } - copy`;
}

export function offerCopyForDraft(offer: Offer) {
	const draft = offerCopy(offer);
	draft.header.id = undefined;
	draft.header.externalId = undefined;
	draft.header.rfqRef = createNextRfqRef(offer.header.rfqRef);
	draft.header.periodStartMonth = undefined;
	draft.header.periodEndMonth = undefined;
	if (draft.mapproducts) {
		for (const product of draft.mapproducts) {
			product.id = undefined;
			product.externalId = undefined;
			product.rdd = undefined;
			product.price = undefined;
			product.offerStatus = 'DRAFT';
			product.feasibilityStatus = 'PENDING';
		}
	}
	return draft;
}
